"use client";

import { useSyncExternalStore } from "react";

// Consentement cookies — le « portier » de la mesure d'audience.
//
// Règle légale (art. 82 loi Informatique et Libertés) : rien ne peut être déposé
// ni lu sur l'appareil du visiteur à des fins de mesure tant qu'il n'a pas dit oui.
// Conséquence technique : PostHog n'est PAS démarré au chargement de la page — il
// n'est allumé que par `setConsent("granted")` (voir `lib/analytics.ts`).
//
// Le choix lui-même est mémorisé en localStorage : c'est le mécanisme du
// consentement, pas un traceur, donc il n'a pas besoin d'être consenti.

const KEY = "atlanx-consent";

/** 6 mois : au-delà, on redemande (recommandation CNIL). */
const MAX_AGE_MS = 182 * 24 * 60 * 60 * 1000;

export type ConsentState = "granted" | "denied" | "unknown";

type Stored = { v: 1; analytics: boolean; at: number };

/** Le bandeau et la mesure d'audience vivent dans deux composants différents :
 *  cet événement les tient synchronisés sans rechargement de page. */
const EVENT = "atlanx:consent";

export function readConsent(): ConsentState {
  if (typeof window === "undefined") return "unknown";
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return "unknown";
    const s = JSON.parse(raw) as Stored;
    if (s.v !== 1 || typeof s.analytics !== "boolean") return "unknown";
    if (Date.now() - s.at > MAX_AGE_MS) return "unknown"; // périmé → on redemande
    return s.analytics ? "granted" : "denied";
  } catch {
    return "unknown";
  }
}

export function setConsent(state: "granted" | "denied"): void {
  try {
    const stored: Stored = { v: 1, analytics: state === "granted", at: Date.now() };
    localStorage.setItem(KEY, JSON.stringify(stored));
  } catch {
    /* navigation privée bloquée : on applique quand même le choix pour cette visite */
  }
  window.dispatchEvent(new CustomEvent(EVENT, { detail: state }));
}

/** « Gérer mes cookies » (pied de page) : oublie le choix pour reposer la question. */
export function resetConsent(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* rien à oublier */
  }
  window.dispatchEvent(new CustomEvent(EVENT, { detail: "unknown" }));
}

function subscribe(onChange: () => void): () => void {
  window.addEventListener(EVENT, onChange);
  return () => window.removeEventListener(EVENT, onChange);
}

/**
 * Le choix de consentement, lu en direct. `null` = pas encore su : le rendu
 * serveur ne peut pas connaître le navigateur, donc rien ne s'affiche tant que
 * React n'a pas repris la main côté client (pas de bandeau qui clignote chez
 * quelqu'un qui a déjà répondu, et pas d'erreur d'hydratation).
 */
export function useConsent(): ConsentState | null {
  return useSyncExternalStore(subscribe, readConsent, () => null);
}

/** Efface les cookies déposés par PostHog (`ph_*`) — utilisé quand on retire son accord. */
export function clearAnalyticsCookies(): void {
  const host = window.location.hostname;
  // On efface sur le domaine exact ET sur le domaine parent : PostHog pose le sien
  // sur `.atlanx.io` pour couvrir les sous-domaines.
  const domains = ["", host, `.${host}`, `.${host.split(".").slice(-2).join(".")}`];
  for (const cookie of document.cookie.split(";")) {
    const name = cookie.split("=")[0]?.trim();
    if (!name?.startsWith("ph_")) continue;
    for (const d of domains) {
      document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT${d ? `; domain=${d}` : ""}`;
    }
  }
}
